<script setup lang="ts">
import { ref } from 'vue'

import BaseDialog from '../ui/components/BaseDialog.vue'
import BaseField from '../ui/components/BaseField.vue'
import { useDialog } from '../ui/dialogs'

export interface AddUserPayload {
  email:    string
  password: string
  notes:    string
}

/**
 * The caller passes the async submit fn so the dialog stays open during the
 * call (with disabled inputs and a "Creating…" primary label) and closes on
 * success. Returning false leaves the dialog open — the caller shows the
 * failure (currently via useAdmin's `error` ref surfaced in AdminView).
 */
const props = defineProps<{
  create: (payload: AddUserPayload) => Promise<boolean>
}>()

const { close } = useDialog<AddUserPayload>()

/** Mirrors MIN_PASSWORD_LENGTH in functions/src/index.ts. */
const MIN_PASSWORD = 12

const email    = ref('')
const password = ref('')
const notes    = ref('')
const busy       = ref(false)
const localError = ref<string | null>(null)

/** Suggests a strong temporary password so admins don't invent weak ones. */
function generatePassword() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_'
  const bytes = new Uint32Array(20)
  crypto.getRandomValues(bytes)
  password.value = Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}

async function onSubmit() {
  localError.value = null
  if (password.value.length < MIN_PASSWORD) {
    // Checked here too so the admin gets the message without a round-trip; the
    // function enforces it regardless.
    localError.value = `Password must be at least ${MIN_PASSWORD} characters.`
    return
  }
  const payload: AddUserPayload = {
    email:    email.value.trim(),
    password: password.value,
    notes:    notes.value.trim(),
  }
  busy.value = true
  try {
    if (await props.create(payload)) close(payload)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <BaseDialog title="Add user" @dismiss="close()">
    <p class="hint">
      The new account is active immediately and is never an admin. Send the
      temporary password to the user through a channel you trust.
    </p>

    <form id="add-user-form" @submit.prevent="onSubmit">
      <BaseField label="Email">
        <template #default="{ id }">
          <input :id="id" v-model="email" type="email" required :disabled="busy" autocomplete="off" class="input" />
        </template>
      </BaseField>

      <BaseField label="Temporary password">
        <template #default="{ id }">
          <div class="pw-row">
            <input
              :id="id"
              v-model="password"
              type="text"
              required
              :disabled="busy"
              autocomplete="off"
              :minlength="MIN_PASSWORD"
              class="input pw-input"
            />
            <button type="button" class="btn btn-secondary" :disabled="busy" @click="generatePassword">
              Generate
            </button>
          </div>
        </template>
      </BaseField>

      <BaseField label="Notes" hint="Optional">
        <template #default="{ id }">
          <input :id="id" v-model="notes" type="text" :disabled="busy" autocomplete="off" class="input" />
        </template>
      </BaseField>

      <p v-if="localError" class="field-error" role="alert">{{ localError }}</p>
    </form>

    <template #actions>
      <button type="button" class="btn btn-secondary" :disabled="busy" @click="close()">
        Cancel
      </button>
      <button type="submit" form="add-user-form" class="btn btn-primary" :disabled="busy">
        {{ busy ? 'Creating…' : 'Create user' }}
      </button>
    </template>
  </BaseDialog>
</template>

<style scoped>
/* Unique to this dialog: two-column row combining the password input with
   its "Generate" button, and a monospace font for the generated value. */
.pw-row {
  display: flex;
  gap: var(--sp-2);
}

.pw-input {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
}

form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}
</style>
